import React, { useEffect, useState } from "react";
import { Box, HStack, Select, Text, Flex } from "@chakra-ui/react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import CustomDropdown from "../../components/CustomDropdown";
import {
  getYear,
  format,
  getDaysInMonth,
  addDays,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import DonutChartStatus from "./DonutChartStatus";
import { fetchCheckInRecordsForYear } from "../../utils/checkInOutUtils";
import MonthlyBurnoutPie from "./MonthlyBurnoutPieChart"; // Adjust path as needed
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";

interface CheckInCheckOutData {
  checkInAddress: string;
  checkInPhotoUri: string;
  checkInTime: string;
  checkOutAddress: string;
  checkOutPhotoUri: string;
  checkOutTime: string;
  duration: string;
  isCheckedOut: boolean;
  taskUpdate?: string;
  workMode?: "Office" | "Home";
}

interface CheckOutProps {
  organizationId: string;
  userId: string;
}

const CheckoutComingSoon: React.FC<CheckOutProps> = ({
  organizationId,
  userId,
}) => {

  const now = new Date();
  const currentYear = getYear(now);
  const [selectedWeek, setSelectedWeek] = useState("1");

  const [checkInOutData, setCheckInOutData] = useState<
    CheckInCheckOutData[] | null
  >(null);
  const [filterType, setFilterType] = useState("date");

  const [selectedDay, setSelectedDay] = useState<string>(
    now.getDate().toString()
  );
  const [selectedMonth, setSelectedMonth] = useState<string>(
    format(now, "MMMM")
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString()
  );
  const [selectedDateFilter, setSelectedDateFilter] = useState<Date | null>(
    null
  );

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    format(new Date(0, i), "MMMM")
  );
  const recentYears = Array.from({ length: 5 }, (_, i) =>
    (currentYear - i).toString()
  );

  const getWeekRange = (year: number, monthIndex: number, weekNum: number) => {
    const monthStart = startOfMonth(new Date(year, monthIndex));
    const firstWeekStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const start = addDays(firstWeekStart, (weekNum - 1) * 7);
    const end = endOfWeek(start, { weekStartsOn: 1 });
    return { start, end };
  };

  const getWeeklyEntries = () => {
    if (!checkInOutData) return [];
    const monthIndex = monthNames.indexOf(selectedMonth);
    const yearNum = Number(selectedYear);
    const weekNum = Number(selectedWeek);

    const { start, end } = getWeekRange(yearNum, monthIndex, weekNum);

    return checkInOutData.filter((entry) => {
      const checkInDate = new Date(entry.checkInTime);
      return checkInDate >= start && checkInDate <= end;
    });
  };

  const parseDuration = (durationStr: string): number => {
    const hrMatch = durationStr.match(/(\d+)\s*hr/);
    const minMatch = durationStr.match(/(\d+)\s*min/);
    const hr = hrMatch ? parseInt(hrMatch[1], 10) : 0;
    const min = minMatch ? parseInt(minMatch[1], 10) : 0;
    return hr + min / 60;
  };

  const getFilteredDurationsForDate = (): number[] => {
    if (!checkInOutData || !selectedDateFilter) return [];

    const selectedStr = format(selectedDateFilter, "yyyy-MM-dd");

    return checkInOutData
      .filter((entry) => {
        const checkInStr = format(new Date(entry.checkInTime), "yyyy-MM-dd");
        return checkInStr === selectedStr;
      })
      .map((entry) => {
        if (entry.isCheckedOut) {
          return parseDuration(entry.duration);
        } else {
          const checkIn = new Date(entry.checkInTime);
          const now = new Date();
          const diffMs = now.getTime() - checkIn.getTime();
          return diffMs / 1000 / 60 / 60;
        }
      });
  };

  const calculateMonthlyStats = (): {
    burnout: number;
    good: number;
    under: number;
    none: number;
    totalWorkHours: number;
    totalWorkDays: number;
  } => {
    const result = {
      burnout: 0,
      good: 0,
      under: 0,
      none: 0,
      totalWorkHours: 0,
      totalWorkDays: 0,
    };
    if (!checkInOutData) return result;

    const selectedMonthIndex = monthNames.indexOf(selectedMonth);
    const selectedYearNum = Number(selectedYear);
    const daysInMonth = getDaysInMonth(
      new Date(selectedYearNum, selectedMonthIndex)
    );

    const grouped: { [key: string]: number } = {};

    checkInOutData.forEach((entry) => {
      const date = new Date(entry.checkInTime);
      if (
        date.getFullYear() === selectedYearNum &&
        date.getMonth() === selectedMonthIndex
      ) {
        const key = format(date, "yyyy-MM-dd");
        const duration = entry.isCheckedOut
          ? parseDuration(entry.duration)
          : (new Date().getTime() - new Date(entry.checkInTime).getTime()) /
          1000 /
          60 /
          60;

        grouped[key] = (grouped[key] || 0) + duration;
      }
    });

    Object.values(grouped).forEach((hrs) => {
      result.totalWorkHours += hrs;
      if (hrs < 8) result.under++;
      else if (hrs > 9) result.burnout++;
      else result.good++;
    });

    const daysLogged = Object.keys(grouped).length;
    result.totalWorkDays = result.burnout + result.good + result.under;
    result.none = daysInMonth - daysLogged;

    return result;
  };

  const filteredMonthlyEntries =
    checkInOutData?.filter((entry) => {
      const d = new Date(entry.checkInTime);
      return (
        d.getFullYear() === Number(selectedYear) &&
        d.getMonth() === monthNames.indexOf(selectedMonth)
      );
    }) || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch by the selected year, not a fixed trailing window - the Date/
        // Week/Month/Year filters above let the user pick any of the last 5
        // years, and a window relative to "today" would silently miss any
        // month outside it (e.g. picking May while today is in August).
        const logs = await fetchCheckInRecordsForYear(organizationId, userId, Number(selectedYear));
        // Auto-checked-out days flagged absent shouldn't count as worked
        // time here - drop them so the Summary/Detail Logs tabs fall back
        // to their normal "no data" states instead of showing that duration.
        const filteredLogs = (logs as any[]).filter((entry) => !entry.markedAsAbsent);
        setCheckInOutData(filteredLogs as any);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    fetchData();
  }, [organizationId, userId, selectedYear]);



  const calculateYearlyStats = (): {
    burnout: number;
    good: number;
    under: number;
    none: number;
    totalWorkHours: number;
    totalWorkDays: number;
  } => {
    const result = {
      burnout: 0,
      good: 0,
      under: 0,
      none: 0,
      totalWorkHours: 0,
      totalWorkDays: 0,
    };
    if (!checkInOutData) return result;

    const selectedYearNum = Number(selectedYear);
    const grouped: { [key: string]: number } = {};

    checkInOutData.forEach((entry) => {
      const date = new Date(entry.checkInTime);
      if (date.getFullYear() === selectedYearNum) {
        const key = format(date, "yyyy-MM-dd");
        const duration = entry.isCheckedOut
          ? parseDuration(entry.duration)
          : (new Date().getTime() - new Date(entry.checkInTime).getTime()) /
          1000 /
          60 /
          60;

        grouped[key] = (grouped[key] || 0) + duration;
      }
    });

    Object.values(grouped).forEach((hrs) => {
      result.totalWorkHours += hrs;
      if (hrs === 0) result.none++;
      else if (hrs < 8) result.under++;
      else if (hrs > 9) result.burnout++;
      else result.good++;
    });

    result.totalWorkDays = result.burnout + result.good + result.under;
    result.none = 365 - Object.keys(grouped).length;

    return result;
  };

  useEffect(() => {
    if (filterType === "date" && selectedDay && selectedMonth && selectedYear) {
      const monthIndex = monthNames.indexOf(selectedMonth);
      const daysInSelectedMonth = getDaysInMonth(new Date(Number(selectedYear), monthIndex));

      // If current selected day is invalid for the new month (e.g., Feb 30), reset to the last day of the month
      if (Number(selectedDay) > daysInSelectedMonth) {
        setSelectedDay(daysInSelectedMonth.toString());
        return;
      }

      const fullDate = new Date(
        Number(selectedYear),
        monthIndex,
        Number(selectedDay)
      );
      setSelectedDateFilter(fullDate);
    }
  }, [selectedDay, selectedMonth, selectedYear, filterType]);

  const calculateWeeklyStats = (): {
    burnout: number;
    good: number;
    under: number;
    none: number;
    totalWorkHours: number;
    totalWorkDays: number;
  } => {
    const result = {
      burnout: 0,
      good: 0,
      under: 0,
      none: 0,
      totalWorkHours: 0,
      totalWorkDays: 0,
    };

    if (!checkInOutData) return result;

    const monthIndex = monthNames.indexOf(selectedMonth);
    const yearNum = Number(selectedYear);
    const weekNum = Number(selectedWeek);

    const { start, end } = getWeekRange(yearNum, monthIndex, weekNum);

    const grouped: { [key: string]: number } = {};

    checkInOutData.forEach((entry) => {
      const date = new Date(entry.checkInTime);
      if (date >= start && date <= end) {
        const key = format(date, "yyyy-MM-dd");
        const duration = entry.isCheckedOut
          ? parseDuration(entry.duration)
          : (new Date().getTime() - date.getTime()) / 1000 / 60 / 60;

        grouped[key] = (grouped[key] || 0) + duration;
      }
    });

    Object.values(grouped).forEach((hrs) => {
      result.totalWorkHours += hrs;
      if (hrs < 8) result.under++;
      else if (hrs > 9) result.burnout++;
      else result.good++;
    });

    result.totalWorkDays = result.burnout + result.good + result.under;
    const totalDaysInRange = 7;
    result.none = totalDaysInRange - result.totalWorkDays;

    return result;
  };


  const calculateOverallStats = (): {
    burnout: number;
    good: number;
    under: number;
    none: number;
    totalWorkHours: number;
    totalWorkDays: number;
  } => {
    const result = {
      burnout: 0,
      good: 0,
      under: 0,
      none: 0,
      totalWorkHours: 0,
      totalWorkDays: 0,
    };

    if (!checkInOutData) return result;

    const grouped: { [key: string]: number } = {};

    checkInOutData.forEach((entry) => {
      const date = new Date(entry.checkInTime);
      const key = format(date, "yyyy-MM-dd");
      const duration = entry.isCheckedOut
        ? parseDuration(entry.duration)
        : (new Date().getTime() - date.getTime()) / 1000 / 60 / 60;

      grouped[key] = (grouped[key] || 0) + duration;
    });

    Object.values(grouped).forEach((hrs) => {
      result.totalWorkHours += hrs;
      if (hrs < 8) result.under++;
      else if (hrs > 9) result.burnout++;
      else result.good++;
    });

    result.totalWorkDays = result.burnout + result.good + result.under;
    if (result.totalWorkDays === 0) {
      result.none = 1;
    } // You could calculate gaps across a full date range if needed.

    return result;
  };


  const resetToCurrent = () => {
    setSelectedDay(now.getDate().toString());
    setSelectedMonth(format(now, "MMMM"));
    setSelectedYear(currentYear.toString());
    setSelectedDateFilter(null);
  };

  // Check if selected date is today
  const isTodaySelected = () => {
    if (filterType !== "date") return false;
    return (
      selectedDay === now.getDate().toString() &&
      selectedMonth === format(now, "MMMM") &&
      selectedYear === currentYear.toString()
    );
  };

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  return (
    <Box mt={4} p={4}>
      <Flex
        wrap="wrap"
        gap={3}
        align="center"
        justify="flex-start"
        p={4}
        className={`${isDark ? theme.card : 'bg-white'} border ${theme.border} rounded-md`}
      >
        {/* 🔹 Filter Type */}
        <Text fontSize="sm" fontWeight="medium" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
          Filter:
        </Text>
        <CustomDropdown
          value={filterType}
          onChange={(value) => {
            setFilterType(value);
            resetToCurrent();
          }}
          isDark={isDark}
          className="max-w-[100px] h-8 text-sm"
          options={[
            { value: "", label: "select" },
            { value: "date", label: "Date" },
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
            { value: "year", label: "Year" }
          ]}
        />

        {/* 🔸 Date Filter: Day + Month + Year */}
        {filterType === "date" && (
          <>
            <CustomDropdown
              value={selectedDay}
              onChange={(value) => setSelectedDay(value)}
              isDark={isDark}
              className="max-w-[70px] h-8 text-sm"
              options={Array.from(
                { length: getDaysInMonth(new Date(Number(selectedYear), monthNames.indexOf(selectedMonth))) },
                (_, i) => i + 1
              ).map((day) => ({
                value: day.toString(),
                label: day.toString()
              }))}
            />

            <CustomDropdown
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              isDark={isDark}
              className="w-[120px] h-8 text-sm"
              options={monthNames.map((month) => ({
                value: month,
                label: month
              }))}
            />

            <CustomDropdown
              value={selectedYear}
              onChange={(value) => setSelectedYear(value)}
              isDark={isDark}
              className="max-w-[90px] h-8 text-sm"
              options={recentYears.map((year) => ({
                value: year,
                label: year
              }))}
            />
          </>
        )}

        {filterType === "week" && (
          <>
            <CustomDropdown
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              isDark={isDark}
              className="w-[120px] h-8 text-sm"
              options={monthNames.map((month) => ({
                value: month,
                label: month
              }))}
            />

            <CustomDropdown
              value={selectedYear}
              onChange={(value) => setSelectedYear(value)}
              isDark={isDark}
              className="max-w-[90px] h-8 text-sm"
              options={recentYears.map((year) => ({
                value: year,
                label: year
              }))}
            />

            <CustomDropdown
              value={selectedWeek}
              onChange={(value) => setSelectedWeek(value)}
              isDark={isDark}
              className="max-w-[90px] h-8 text-sm"
              options={[1, 2, 3, 4, 5].map((week) => ({
                value: week.toString(),
                label: `Week ${week}`
              }))}
            />
          </>
        )}

        {/* 🔸 Month Filter */}
        {filterType === "month" && (
          <>
            <CustomDropdown
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              isDark={isDark}
              className="w-[120px] h-8 text-sm"
              options={monthNames.map((month) => ({
                value: month,
                label: month
              }))}
            />

            <CustomDropdown
              value={selectedYear}
              onChange={(value) => setSelectedYear(value)}
              isDark={isDark}
              className="max-w-[90px] h-8 text-sm"
              options={recentYears.map((year) => ({
                value: year,
                label: year
              }))}
            />
          </>
        )}

        {/* 🔸 Year Filter */}
        {filterType === "year" && (
          <CustomDropdown
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
            isDark={isDark}
            className="max-w-[90px] h-8 text-sm"
            options={recentYears.map((year) => ({
              value: year,
              label: year
            }))}
          />
        )}

        {/* 🔹 Overall */}
        <button
          onClick={() => { setFilterType("overall"); resetToCurrent(); }}
          className={`px-3 py-1.5 rounded-full text-sm ${filterType === 'overall' ? theme.primaryButton : (isDark ? 'bg-[#2A3441] text-white border border-[#2E2F33] hover:bg-[#3A4553]' : 'bg-white text-[#B59143] border border-[#B59143] hover:bg-[#FFF7E6]')} transition-colors`}
        >
          Overall
        </button>

        {/* 🔹 Reset - Hide when today's date is selected */}
        {!isTodaySelected() && (
          <button
            onClick={() => { setFilterType("date"); resetToCurrent(); }}
            className="px-3 py-1.5 rounded-md text-sm border border-red-300 text-red-600 hover:bg-red-50"
          >
            Reset to today
          </button>
        )}
      </Flex>

      {filterType === "date" && (
        <DonutChartStatus
          durations={getFilteredDurationsForDate()}
          entries={
            checkInOutData?.filter((entry) => {
              const checkInDate = new Date(entry.checkInTime);
              return (
                checkInDate.getDate() === selectedDateFilter?.getDate() &&
                checkInDate.getMonth() === selectedDateFilter?.getMonth() &&
                checkInDate.getFullYear() === selectedDateFilter?.getFullYear()
              );
            }) || []
          }
        />
      )}

      {filterType === "month" && checkInOutData && (
        <Box mt={8}>
          <MonthlyBurnoutPie
            stats={calculateMonthlyStats()}
            entries={filteredMonthlyEntries}
          />
        </Box>
      )}

      {filterType === "year" && checkInOutData && (
        <Box mt={8}>
          <MonthlyBurnoutPie
            stats={calculateYearlyStats()}
            entries={checkInOutData.filter((entry) => {
              const checkInDate = new Date(entry.checkInTime);
              return checkInDate.getFullYear() === Number(selectedYear);
            })}
          />
        </Box>
      )}

      {filterType === "week" && checkInOutData && (
        <Box mt={8}>
          <MonthlyBurnoutPie
            stats={calculateWeeklyStats()}
            entries={getWeeklyEntries()}
          />
        </Box>
      )}

      {filterType === "overall" && checkInOutData && (
        <Box mt={8}>
          <MonthlyBurnoutPie
            stats={calculateOverallStats()}
            entries={checkInOutData}
            mode="overall"
          />
        </Box>
      )}
    </Box>
  );
};

export default CheckoutComingSoon;
